import React, { useState, useEffect } from 'react';
import {
  Box,
  makeStyles,
  Typography,
  TextField,
  Paper,
  InputBase,
  IconButton,
  Button,
  Grid,
  Checkbox,
} from '@material-ui/core';
import moment from 'moment';
import { Listing } from 'models';
import { useAlert, useAuth } from 'hooks';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { startsWith } from 'lodash';
import { HvPleaseWait, HvIconButton, HvModal } from 'components';
import CloseIcon from '@material-ui/icons/Close';
import { MODAL, ALERT, COLOR } from 'config/constants';
import { isEmailValid } from 'utils';
import { useDispatch } from 'react-redux';
import { useListingSendMedia } from 'hooks/listings';
import { hideModal } from 'state/actions';
import { useIntl } from 'react-intl';
import { images } from 'assets/images';
import { isMobile } from 'utils/screen';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: 5,
    justifyContent: 'center',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    '@media (max-width: 767px)': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 15,
  },
  subtitle: {
    fontWeight: 'bold',
    marginTop: 15,
  },
  button: {
    backgroundColor: COLOR.LIGHT_BLUE,
    color: COLOR.WHITE,
    marginLeft: 10,
    height: 40,
    width: 100,
    '@media (max-width: 767px)': {
      marginLeft: 0,
      marginTop: 5,
      width: '100%',
    },
  },
  input: {
    flex: 1,
    height: '100%',
    margin: 'auto 10px',
  },
  copyInput: {
    width: '100%',
    marginBottom: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: COLOR.ULTRA_LIGHT_GREY,
  },
  copyIcon: {
    width: 30,
    height: 30,
  },
  logs: {
    maxHeight: 100,
    overflowY: 'scroll',
    marginTop: 20,
  },
  logText: {
    fontSize: 11,
    marginBottom: 5,
    color: COLOR.DARK_GREY,
  },
  closeButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    color: COLOR.DARK_GREY,
  },
  langButton: {
    '&.MuiButton-root': {
      minWidth: 30,
      height: 30,
    },
    '&:hover, &:focus': {
      backgroundColor: COLOR.LIGHT_GREY,
    },
    backgroundColor: COLOR.LIGHT_GREY,
  },
  selectedLangButton: {
    height: 30,
    backgroundColor: COLOR.LIGHT_BLUE,
    '&:hover, &:focus': {
      backgroundColor: COLOR.LIGHT_BLUE,
    },
    color: COLOR.WHITE,
    '&.MuiButton-root': {
      minWidth: 30,
    },
  },
  nameContent: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 10,
    width: '100%',
    justifyContent: 'space-between',
  },
  firstnameInput: {
    width: '45%',
    '@media (max-width: 767px)': {
      width: '100%',
    },
  },
  lastnameInput: {
    width: '45%',
    '@media (max-width: 767px)': {
      width: '100%',
      marginTop: 10,
    },
  },
});

interface ParamTypes {
  token: string;
}
interface Props {
  listing: Listing;
}
function ShareMediaModal({ listing }: Props) {
  const styles = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { displayAlert } = useAlert();
  const { token } = useAuth();

  const [client_firstname, setClientFirstname] = useState('');
  const [client_lastname, setClientLastname] = useState('');
  const [can_be_downloaded, setCanBeDownloaded] = useState(false);
  const [email, setEmail] = useState('');
  const [phone_number, setPhoneNumber] = useState('');
  const [is_phone_number_valid, setPhoneNumberValid] = useState(false);
  const [public_media_url, setPublicMediaUrl] = useState('');
  const [isCopied, setCopied] = useState(false);
  const [load, setLoad] = useState(true);
  const [selectedLang, setSelectedLang] = useState('fr');

  const { sendMediaEmail, sentEmailStatus, sendMediaSMS, sentSMSStatus, mediaNotificationLogs } =
    useListingSendMedia(token, listing.id, listing.broker_id);

  const fmtShareMediaTitle = intl.formatMessage({ id: 'deliver.shareModal.title' });
  const fmtEmail = intl.formatMessage({ id: 'deliver.shareModal.email' });
  const fmtEmailSent = intl.formatMessage({ id: 'deliver.shareModal.emailSent' });
  const fmtShare = intl.formatMessage({ id: 'deliver.shareModal.share' });
  const fmtShareLink = intl.formatMessage({ id: 'deliver.shareModal.shareLink' });
  const fmtCopied = intl.formatMessage({ id: 'deliver.shareModal.copied' });
  const fmtInvalidEmail = intl.formatMessage({
    id: 'deliver.shareModal.invalidEmailAddress',
  });
  const fmtEmptyEmail = intl.formatMessage({ id: 'deliver.shareModal.emptyEmail' });
  const fmtAt = intl.formatMessage({ id: 'deliver.shareModal.at' });
  const fmtEmailSentSuccess = intl.formatMessage({ id: 'deliver.shareModal.emailSentSuccess' });
  const fmtSendMessage = intl.formatMessage({ id: 'deliver.shareModal.sendMessage' });
  const fmtPhoneNumber = intl.formatMessage({ id: 'deliver.shareModal.phoneNumber' });
  const fmtEmptyPhoneNumber = intl.formatMessage({ id: 'deliver.shareModal.emptyPhoneNumber' });
  const fmtInvalidPhoneNumber = intl.formatMessage({ id: 'deliver.shareModal.invalidPhoneNumber' });
  const fmtMessageSent = intl.formatMessage({ id: 'deliver.shareModal.messageSent' });
  const fmtLanguage = intl.formatMessage({ id: 'deliver.shareModal.language' });
  const fmtFirstname = intl.formatMessage({ id: 'deliver.shareModal.firstname' });
  const fmtLastname = intl.formatMessage({ id: 'deliver.shareModal.lastname' });
  const fmtEmptyFirstname = intl.formatMessage({ id: 'deliver.shareModal.emptyFirstname' });
  const fmtEmptyLastname = intl.formatMessage({ id: 'deliver.shareModal.emptyLastname' });
  const fmtDownloadAllowed = intl.formatMessage({ id: 'deliver.shareModal.downloadAllowed' });
  useEffect(() => {
    setLoad(false);
    setPublicMediaUrl(
      `https://media.hausvalet.ca/public-media-page/${listing?.distribution_token}`,
    );
  }, []);

  useEffect(() => {
    if (can_be_downloaded) {
      setPublicMediaUrl(
        `https://media.hausvalet.ca/public-media-page/${listing?.distribution_token}?download=true`,
      );
    }
  }, [can_be_downloaded]);

  useEffect(() => {
    if (sentEmailStatus === 200) {
      displayAlert(ALERT.SUCCESS, fmtEmailSentSuccess);
    }
  }, [sentEmailStatus]);

  useEffect(() => {
    if (sentSMSStatus === 200) {
      displayAlert(ALERT.SUCCESS, fmtMessageSent);
    }
  }, [sentSMSStatus]);

  const clearValues = () => {
    setEmail('');
    setClientFirstname('');
    setClientLastname('');
    setPhoneNumber('');
  };

  const onClose = () => {
    dispatch(hideModal(MODAL.SHARE));
    clearValues();
  };

  const onCopyURL = (url: string) => {
    return () => {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setCopied(true);
          console.log('text copied to clipboard');
        })
        .catch((err) => {
          console.log('error copy to clipboard', err);
        });
    };
  };

  const onChangeEmail = (e: any) => {
    setEmail(e.target.value);
  };

  const onChangeCheckbox = (e: any) => {
    setCanBeDownloaded(e.target.checked);
    setCopied(false);
  };

  const onShare = () => {
    if (client_firstname === '') {
      displayAlert(ALERT.ERROR, fmtEmptyFirstname);
    } else if (client_lastname === '') {
      displayAlert(ALERT.ERROR, fmtEmptyLastname);
    } else if (email === '') {
      displayAlert(ALERT.ERROR, fmtEmptyEmail);
    } else if (!isEmailValid(email)) {
      displayAlert(ALERT.ERROR, fmtInvalidEmail);
      clearValues();
    } else {
      sendMediaEmail(email, client_firstname, client_lastname, selectedLang, public_media_url);
      clearValues();
    }
  };

  const onChangePhoneNumber = (value, country, e, formattedValue) => {
    const expectedLength =
      country && country.format && country.format.replace(/[+() -]/g, '').length;
    setPhoneNumberValid(value.length === expectedLength);
    setPhoneNumber(value);
  };

  const validatePhoneNumber = (inputNumber, country, countries) => {
    const is_valid = countries.some(() => {
      return startsWith(inputNumber, country.dialCode) || startsWith(country.dialCode, inputNumber);
    });
    return is_valid;
  };

  const onSendSMS = () => {
    if (client_firstname === '') {
      displayAlert(ALERT.ERROR, fmtEmptyFirstname);
    } else if (client_lastname === '') {
      displayAlert(ALERT.ERROR, fmtEmptyLastname);
    } else if (phone_number === '') {
      displayAlert(ALERT.ERROR, fmtEmptyPhoneNumber);
    } else if (!is_phone_number_valid) {
      displayAlert(ALERT.ERROR, fmtInvalidPhoneNumber);
      clearValues();
    } else {
      sendMediaSMS(client_firstname, client_lastname, phone_number, selectedLang, public_media_url);
      clearValues();
    }
  };

  const modalContent = (
    <Box className={styles.content}>
      <IconButton className={styles.closeButton} onClick={onClose}>
        <CloseIcon />
      </IconButton>
      <img src={images.shareBg} alt='share with clients' />
      <Typography className={styles.title}>{fmtShareMediaTitle}</Typography>
      <Box className={styles.row}>
        <Paper className={styles.copyInput}>
          <InputBase
            className={styles.input}
            type='text'
            defaultValue={public_media_url}
            readOnly
          />
          <IconButton onClick={onCopyURL(public_media_url)}>
            {isCopied ? (
              <Typography>{fmtCopied}</Typography>
            ) : (
              <img src={images.copy_blue} className={styles.copyIcon} alt='copy' />
            )}
          </IconButton>
        </Paper>
      </Box>
      <Box className={styles.row}>
        <Typography>{fmtDownloadAllowed}</Typography>
        <Checkbox checked={can_be_downloaded} onChange={onChangeCheckbox} />
      </Box>
      <Box className={styles.row}>
        <TextField
          required
          variant='outlined'
          size='small'
          className={styles.firstnameInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            return setClientFirstname(e.target.value);
          }}
          value={client_firstname || ''}
          label={fmtFirstname}
        />
        <TextField
          required
          variant='outlined'
          className={styles.lastnameInput}
          size='small'
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            return setClientLastname(e.target.value);
          }}
          value={client_lastname || ''}
          label={fmtLastname}
        />
      </Box>
      <Box className={styles.row}>
        <Typography>{fmtLanguage}</Typography>
        <Box>
          <Button
            className={selectedLang === 'fr' ? styles.selectedLangButton : styles.langButton}
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            onClick={() => {
              setSelectedLang('fr');
            }}
          >
            <Typography>fr</Typography>
          </Button>
          <Button
            className={selectedLang === 'en' ? styles.selectedLangButton : styles.langButton}
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            onClick={() => {
              setSelectedLang('en');
            }}
          >
            <Typography>en</Typography>
          </Button>
        </Box>
      </Box>
      <Box>
        <Box className={styles.row}>
          <TextField
            fullWidth
            variant='outlined'
            size='small'
            label={fmtEmail}
            onChange={onChangeEmail}
            value={email}
          />
          <HvIconButton className={styles.button} icon='email' text={fmtShare} onClick={onShare} />
        </Box>
        <Box className={styles.row}>
          <PhoneInput
            country='ca'
            preferredCountries={['ca', 'us']}
            enableSearch
            value={`+${phone_number}`}
            placeholder={fmtPhoneNumber}
            inputProps={{
              name: fmtPhoneNumber,
              required: false,
            }}
            prefix='+'
            countryCodeEditable={false}
            onChange={(value, country, e, formattedValue) => {
              return onChangePhoneNumber(value, country, e, formattedValue);
            }}
            isValid={(inputNumber, country: any, countries) => {
              return validatePhoneNumber(inputNumber, country, countries);
            }}
            containerStyle={{ marginBottom: 5, marginTop: 5 }}
            inputStyle={{ margin: 0, width: '100%', height: 40 }}
            dropdownStyle={{ color: COLOR.BLACK }}
            defaultErrorMessage={fmtEmptyPhoneNumber}
          />
          <HvIconButton
            className={styles.button}
            icon='message'
            text={fmtSendMessage}
            onClick={onSendSMS}
          />
        </Box>
        <Box className={styles.logs}>
          {mediaNotificationLogs.length > 0 &&
            mediaNotificationLogs.map((log: any) => {
              if (log.email) {
                return (
                  <Typography key={log.id} className={styles.logText}>
                    {`${fmtEmailSent} ${log.client_firstname} ${log.client_lastname} (
                      ${log.email})
                      ${fmtAt}
                      ${moment(log.timestamp).format('h:mm A dddd MMM D YYYY')}`}
                  </Typography>
                );
              }
              if (log.phone_number) {
                return (
                  <Typography key={log.id} className={styles.logText}>
                    {`${fmtMessageSent} ${log.client_firstname} ${log.client_lastname} (
                      ${log.phone_number})
                      ${fmtAt}
                      ${moment(log.timestamp).format('h:mm A dddd MMM D YYYY')}`}
                  </Typography>
                );
              }
              return null;
            })}
        </Box>
      </Box>
    </Box>
  );

  return (
    <HvModal largerWidth={!isMobile()} onClose={onClose}>
      {modalContent}
      {load ? <HvPleaseWait /> : null}
    </HvModal>
  );
}

export default ShareMediaModal;
